<template>
  <div id="profile">
    <!-- ----------- edit profile modal ----------- -->
    <DialogContainer v-if="isEditProfileClicked">
      <div class="adm-dialog edit-dialog">
        <div class="adm-dialog-header">
          <h3>Edit profile</h3>
          <button
            type="button"
            class="adm-dialog-close"
            aria-label="Close"
            @click="cancelForm"
          >
            <i class="fa fa-times" />
          </button>
        </div>
        <form @submit.prevent="updateProfile">
          <div class="adm-dialog-body">
            <div class="edit-avatar">
              <img
                :src="editPreview || profileImageUrl"
                alt="Profile photo"
                class="edit-avatar-img"
              />
              <label class="edit-avatar-upload">
                <i class="fa fa-camera" />
                <span>{{ selectedFile ? selectedFile.name : "Change photo" }}</span>
                <input
                  type="file"
                  accept="image/*"
                  class="edit-avatar-input"
                  @change="handleFileUpload"
                />
              </label>
            </div>
            <div class="edit-field">
              <label for="edit-name" class="edit-label">Full name</label>
              <input
                id="edit-name"
                v-model="editUserForm.fullName"
                class="adm-input"
                placeholder="Your full name"
                required
              />
            </div>
            <div class="edit-field">
              <label for="edit-email" class="edit-label">Email</label>
              <input
                id="edit-email"
                v-model="editUserForm.email"
                type="email"
                class="adm-input"
                placeholder="you@example.com"
                required
              />
              <p class="edit-hint">
                Changing your email will require re-verification.
              </p>
            </div>
          </div>
          <div class="adm-dialog-footer">
            <button
              type="button"
              class="adm-btn-ghost"
              :disabled="isSaving"
              @click="cancelForm"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="adm-btn-primary"
              :disabled="isSaving"
            >
              <i v-if="isSaving" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-check" />
              Save changes
            </button>
          </div>
        </form>
      </div>
    </DialogContainer>

    <!-- ----------- profile card ----------- -->
    <div class="profile-card">
      <div class="profile-hero">
        <img :src="profileImageUrl" alt="Profile photo" class="profile-avatar" />
        <div class="profile-identity">
          <h2 class="profile-name">{{ user.fullName || "—" }}</h2>
          <span
            class="profile-role"
            :class="user.role === 1 ? 'profile-role--admin' : 'profile-role--monitor'"
          >
            <i class="fa fa-shield-halved" />
            {{ user.role === 1 ? "Admin" : "Monitor" }}
          </span>
        </div>
        <button class="adm-btn-primary edit-btn" @click="showEditProfileForm">
          <i class="fa fa-pen" /> Edit profile
        </button>
      </div>

      <div class="profile-fields">
        <div class="profile-field">
          <span class="profile-field-label">Full name</span>
          <span class="profile-field-value">{{ user.fullName || "—" }}</span>
        </div>
        <div class="profile-field">
          <span class="profile-field-label">Email</span>
          <span class="profile-field-value">{{ user.email || "—" }}</span>
        </div>
        <div class="profile-field">
          <span class="profile-field-label">Role</span>
          <span class="profile-field-value">
            {{ user.role === 1 ? "Admin" : "Monitor" }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DialogContainer from "@/components/DialogContainer.vue";
import axios from "axios";
import { API_BASE_URL } from "@/utils/apiBase";
import { getAuthItem, getAuthToken, setAuthItem } from "@/utils/authStorage";
import { avatarUrl } from "@/utils/avatar";

export default {
  components: { DialogContainer },
  data() {
    return {
      isEditProfileClicked: false,
      isSaving: false,
      selectedFile: null,
      editPreview: "",
      // Seeded from the stored session so the header renders immediately;
      // fetchUserProfile fills in the rest.
      user: {
        email: "",
        fullName: getAuthItem("fullName"),
        role: parseInt(getAuthItem("userRole"), 10),
      },
      editUserForm: {
        fullName: "",
        email: "",
      },
      storedImageURL: getAuthItem("profileImageURL"),
      errors: {},
    };
  },
  computed: {
    profileImageUrl() {
      return avatarUrl(this.storedImageURL);
    },
  },
  mounted() {
    this.fetchUserProfile();
  },
  methods: {
    fetchUserProfile() {
      const id = getAuthItem("userID");
      const token = getAuthToken();
      axios
        .get(`${API_BASE_URL}/get-admin/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          this.editUserForm.fullName = response.data.name;
          this.editUserForm.email = response.data.email;
          this.user.email = response.data.email;
          this.user.fullName = response.data.name;
        })
        .catch((error) => {
          console.error("Failed to fetch profile", error);
        });
    },
    showEditProfileForm() {
      this.fetchUserProfile();
      this.editPreview = "";
      this.selectedFile = null;
      this.isEditProfileClicked = true;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.selectedFile = file;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.editPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.editPreview = "";
      }
    },
    async updateProfile() {
      this.isSaving = true;
      const token = getAuthToken();
      const formData = new FormData();
      formData.append("full_name", this.editUserForm.fullName);
      formData.append("admin_users_email", this.editUserForm.email);
      if (this.selectedFile) formData.append("image", this.selectedFile);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/upload-photo/${getAuthItem("userID")}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        this.isEditProfileClicked = false;
        // Writes land in whichever store holds this session, so a "Remember me"
        // login keeps the new photo and name after a browser restart.
        setAuthItem("profileImageURL", response.data.url);
        setAuthItem("fullName", this.editUserForm.fullName);
        this.storedImageURL = response.data.url;
        // Reload so the header pill in HomeView picks up the new values too.
        this.$router.go();
      } catch (error) {
        const data = error.response && error.response.data;
        console.error("Error updating profile:", data || error.message);
        const messages =
          data && data.errors
            ? Object.values(data.errors).flat().join("\n")
            : (data && data.message) || error.message || "Update failed";
        alert(messages);
      } finally {
        this.isSaving = false;
      }
    },
    cancelForm() {
      this.isEditProfileClicked = false;
      this.selectedFile = null;
      this.editPreview = "";
      this.editUserForm = { fullName: "", email: "" };
    },
  },
};
</script>

<style scoped>
#profile {
  width: 100%;
}

.profile-card {
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  padding: 1.75rem;
  max-width: 720px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.profile-hero {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #eef0f4;
}
.profile-avatar {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f7f8fb;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.06);
  flex-shrink: 0;
}
.profile-identity {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.profile-name {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2540;
  word-break: break-word;
}
.profile-role {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.profile-role--admin {
  background: #eef1f7;
  color: #2a345d;
}
.profile-role--monitor {
  background: #f1f3f5;
  color: #5b6577;
}
.edit-btn {
  flex-shrink: 0;
}

.profile-fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding-top: 1.5rem;
}
.profile-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.profile-field-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #98a0ad;
}
.profile-field-value {
  font-size: 0.95rem;
  color: #1f2540;
  word-break: break-word;
}

@media (max-width: 600px) {
  .profile-hero {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
  .profile-avatar {
    width: 80px;
    height: 80px;
  }
}

/* ------ edit profile dialog ------ */
.adm-dialog.edit-dialog {
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}
.adm-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  background: #f7f8fb;
  border-bottom: 1px solid #eaecf2;
}
.adm-dialog-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2540;
}
.adm-dialog-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #5b6577;
  cursor: pointer;
}
.adm-dialog-close:hover {
  background: #eef0f4;
  color: #2a345d;
}
.adm-dialog-body {
  padding: 1.25rem;
}
.adm-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-top: 1px solid #eaecf2;
  background: #ffffff;
}

.edit-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding-bottom: 1.25rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #eef0f4;
}
.edit-avatar-img {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f7f8fb;
}
.edit-avatar-upload {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.9rem;
  background: #f7f8fb;
  border: 1px solid #e6e8ee;
  border-radius: 999px;
  color: #2a345d;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0;
  transition: background 0.15s, border-color 0.15s;
  max-width: 100%;
}
.edit-avatar-upload:hover {
  background: #eef1f7;
  border-color: #d8dde9;
}
.edit-avatar-upload span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 14rem;
}
.edit-avatar-input {
  display: none;
}

.edit-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.9rem;
}
.edit-field:last-child {
  margin-bottom: 0;
}
.edit-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6577;
  margin-bottom: 0.35rem;
}
.edit-hint {
  margin: 0.35rem 0 0 0;
  font-size: 0.75rem;
  color: #98a0ad;
}
</style>
